import { inject, Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { combineLatest, Observable, of } from 'rxjs';
import { map, take, switchMap } from 'rxjs/operators';
import { LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { ClientRequestFragment, ClientRequestItemStatus, ClientRequestResponse, ClientRequestType } from '@aiwyn/api';
import { CxpClientRequestTaskModel } from '../models';

/** Raw CRL row shape with the aliased `project` relation the tasks query adds on top of the fragment. */
type CxpClientRequestForTasksResponse = ClientRequestResponse & { project: { name: string | null } | null };

/**
 * `sys_refs` (jsonb) key set on CRLs ingested from FieldGuide via webhook.
 * Used as the Hasura predicate for "this CRL is FieldGuide-backed" because
 * `vw_client_request` does not expose the underlying `client_request.source`
 * column.
 */
export const CLIENT_REQUEST_FIELDGUIDE_SYSREFS_KEY = 'field_guide';

/**
 * Hasura predicate for "this CRL is the shell of an eSign bundle".
 *
 * An `E_SIGN` request has no `client_request_items` at all — the bundle owns
 * the files and the recipients — so it fails both item-shaped gates below the
 * same way a FieldGuide-backed CRL does, and needs the same escape hatch.
 */
const IS_ESIGN_REQUEST = `{ type: { _eq: "${ClientRequestType.E_SIGN}" } }`;

/**
 * CXP-tasks-owned Hasura fetcher for client requests. Returns the same
 * `ClientRequestModel` shape as the shared client-requests API so the task
 * mapper consumes both interchangeably.
 *
 * `getClientRequestsForTasks` backs the tasks list only while
 * `CXP_ENABLE_UNIFIED_TASKS_ENDPOINTS` is off; with the flag on the list comes
 * from `GET /cxp/tasks/list`. `isClientRequestAccessible` is used on both
 * paths — the lockdown guard runs regardless of the flag.
 *
 * Differs from the shared fetcher in two ways, both targeted at CRLs that carry
 * no items of their own — FieldGuide-backed lists, and `E_SIGN` bundle shells:
 *
 *   1. CRLs whose `sys_refs` carry the FieldGuide marker, and CRLs typed
 *      `E_SIGN`, bypass the "must have at least one non-TO_DO item" activity
 *      filter.
 *   2. Under `ENABLE_CLIENT_REQUEST_ITEM_RESTRICTIONS`, a CRL-level
 *      assignee match is sufficient for those same CRLs (no unrestricted
 *      item required).
 *
 * Owned by the CXP module so query tuning for the tasks surface never
 * affects the shared client-requests fetchers.
 */
@Injectable({ providedIn: 'root' })
export class CxpClientRequestApiService {
  private readonly apollo = inject(Apollo);
  private readonly launchDarklyService = inject(LaunchDarklyService);

  getClientRequestsForTasks(contactIds: number[] | null | undefined): Observable<CxpClientRequestTaskModel[]> {
    if (!contactIds || contactIds.length === 0) return of([]);

    return combineLatest([
      this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_CLIENT_REQUEST_ITEM_RESTRICTIONS),
      this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.CXP_ENABLE_REQUEST_LISTS_LOCKDOWN),
    ]).pipe(
      take(1),
      switchMap(([restrictionsEnabled, lockdownEnabled]) => {
        const whereClause = buildWhereClause(contactIds, restrictionsEnabled, lockdownEnabled);
        const fragment = new ClientRequestFragment(contactIds, [], restrictionsEnabled);

        return this.apollo
          .query<{ clientRequests: CxpClientRequestForTasksResponse[] }>({
            query: gql`
              query GetCxpClientRequestsForTasks {
                clientRequests: vw_client_request(
                  where: { ${whereClause} }
                  order_by: { ordinal: asc }
                ) {
                  ...${fragment.getName()}
                  project: job {
                    name
                  }
                }
              }
              ${fragment.getDependencyFragments()}
            `,
          })
          .pipe(
            map(({ data }) =>
              data.clientRequests.map(
                (cr): CxpClientRequestTaskModel => ({
                  ...fragment.map(cr),
                  projectName: cr.project?.name ?? null,
                }),
              ),
            ),
          );
      }),
    );
  }

  /**
   * Single-CRL accessibility probe for `cxpRequestLockdownGuard`.
   *
   * Returns `true` only when the CRL exists, is active, AND its project is
   * not in an active SUCCESS-or-PENDING lockdown. Out-of-scope, deleted, and locked
   * rows all collapse to `false` — the guard treats them identically
   * (redirect to /main/tasks), so there's no reason to distinguish them.
   */
  isClientRequestAccessible(requestId: number): Observable<boolean> {
    return this.apollo
      .query<{ clientRequests: { id: number }[] }>({
        query: gql`
          query GetCxpClientRequestAccessibility($requestId: Int!) {
            clientRequests: vw_client_request(
              where: {
                id: { _eq: $requestId }
                active: { _eq: true }
                _not: { job_lockdowns: { active: { _eq: true }, status: { _in: ["SUCCESS", "PENDING"] } } }
              }
              limit: 1
            ) {
              id
            }
          }
        `,
        variables: { requestId },
        fetchPolicy: 'network-only',
      })
      .pipe(map(({ data }) => data.clientRequests.length > 0));
  }
}

/**
 * Builds the Hasura where-clause for `getClientRequestsForTasks`.
 *
 * Shape:
 *
 * ```
 * {
 *   active: { _eq: true },
 *   _and: [
 *     // CRL is active (has at least one non-TO_DO item), OR is FieldGuide-backed,
 *     // OR is an eSign bundle shell.
 *     { _or: [ { client_request_sections: { client_request_items: { status: { _neq: "TO_DO" } } } },
 *              { sys_refs: { _has_key: "field_guide" } },
 *              { type: { _eq: "E_SIGN" } } ] },
 *     // Contact is assigned at the CRL level (optionally gated on
 *     // unrestricted-items-or-FieldGuide-or-eSign) OR at any item level.
 *     { _or: [ { client_request_assignee_contacts: { ... } [, _or: [unrestricted, fieldguide, esign]] },
 *              { client_request_sections: { ... client_request_item_assignee.contact_id ... } } ] }
 *   ]
 * }
 * ```
 *
 * Sibling `_or` blocks are wrapped in `_and: [...]` because Hasura's
 * `*_bool_exp` only allows one `_or` per object level.
 */
function buildWhereClause(contactIds: number[], restrictionsEnabled: boolean, lockdownEnabled: boolean): string {
  const lockdownExclusion = lockdownEnabled ? '_not: { job_lockdowns: { active: { _eq: true }, status: { _in: ["SUCCESS", "PENDING"] } } }' : '';

  const itemsExistOrFieldGuideOrEsign = `
    _or: [
      { client_request_sections: { client_request_items: { status: { _neq: "${ClientRequestItemStatus.TO_DO}" } } } },
      { sys_refs: { _has_key: "${CLIENT_REQUEST_FIELDGUIDE_SYSREFS_KEY}" } },
      ${IS_ESIGN_REQUEST}
    ]
  `;

  const crlAssigneeUnrestrictedGate = restrictionsEnabled
    ? `,
      _or: [
        {
          client_request_sections: {
            active: { _eq: true }
            client_request_items: {
              active: { _eq: true }
              restricted: { _eq: false }
            }
          }
        },
        { sys_refs: { _has_key: "${CLIENT_REQUEST_FIELDGUIDE_SYSREFS_KEY}" } },
        ${IS_ESIGN_REQUEST}
      ]
    `
    : '';

  const contactMatch = `
    _or: [
      {
        client_request_assignee_contacts: {
          assignee_contact_id: { _in: [${contactIds}] }
          active: { _eq: true }
        }
        ${crlAssigneeUnrestrictedGate}
      },
      {
        client_request_sections: {
          active: { _eq: true }
          client_request_items: {
            active: { _eq: true }
            client_request_item_assignee: {
              contact_id: { _in: [${contactIds}] }
              active: { _eq: true }
            }
          }
        }
      }
    ]
  `;

  return `
    active: { _eq: true }
    ${lockdownExclusion}
    _and: [
      { ${itemsExistOrFieldGuideOrEsign} },
      { ${contactMatch} }
    ]
  `;
}
